import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Globe,
  Instagram,
  Facebook,
  Sparkles,
} from "lucide-react";
import useSEO from "@/lib/useSEO";
import { useBusiness, usePublicSettings } from "@/contexts/SettingsContext";

export default function Contact() {
  const BUSINESS = useBusiness();
  const settings = usePublicSettings();
  useSEO({
    title: "Contact P & A Cleaning | Sacramento",
    description:
      "Call, text, or email P & A Cleaning (Paula's Cleaning Service) for residential and commercial cleaning in Sacramento and surrounding areas.",
  });

  const items = [
    { icon: Phone, label: "Phone", value: BUSINESS.phone, href: BUSINESS.phoneHref, testid: "contact-phone" },
    { icon: Mail, label: "Email", value: BUSINESS.email, href: `mailto:${BUSINESS.email}`, testid: "contact-email" },
    { icon: MapPin, label: "Service area", value: BUSINESS.area, testid: "contact-area" },
    { icon: Globe, label: "Website", value: BUSINESS.website, href: `https://${BUSINESS.website}`, testid: "contact-website" },
    { icon: Instagram, label: "Instagram", value: BUSINESS.instagram, href: BUSINESS.instagramUrl, testid: "contact-instagram" },
    { icon: Facebook, label: "Facebook", value: BUSINESS.facebook, href: BUSINESS.facebookUrl, testid: "contact-facebook" },
  ];

  return (
    <div data-testid="page-contact">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1E3A8A] shadow-soft">
            <Sparkles className="w-3.5 h-3.5 text-[#34D399]" /> Let's talk
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-[#0F172A] tracking-tight">
            Get in <span className="gradient-text">touch</span>
          </h1>
          <p className="mt-5 text-lg text-[#475569]">
            Call or text, send an email, or request an estimate online — we'll
            get back to you with availability and a per-job quote.
          </p>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map(({ icon: Icon, label, value, href, testid }) => {
              const Wrapper = href ? "a" : "div";
              const wrapperProps = href
                ? { href, target: href.startsWith("http") ? "_blank" : undefined, rel: "noreferrer", "data-testid": testid }
                : { "data-testid": testid };
              return (
                <Wrapper
                  key={label}
                  {...wrapperProps}
                  className="block bg-[#F8FAFC] border border-slate-100 rounded-2xl p-6 hover:shadow-soft hover:-translate-y-0.5 transition-all"
                >
                  <span className="w-11 h-11 rounded-xl bg-white border border-slate-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#1E3A8A]" />
                  </span>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#7C3AED]">
                    {label}
                  </p>
                  <p className="mt-1 font-display font-semibold text-[#0F172A] break-all">
                    {value}
                  </p>
                </Wrapper>
              );
            })}
          </div>

          {settings.license_number && (
            <p
              data-testid="contact-license"
              className="mt-6 text-center text-xs text-[#475569] tracking-wide"
            >
              {settings.license_number}
            </p>
          )}

          {/* CTA bar */}
          <div className="mt-12 rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#8B5CF6] text-white p-8 lg:p-12">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-display font-bold leading-tight">
                  Ready to schedule a clean?
                </h2>
                <p className="mt-2 text-white/80">
                  Tell us about the space — we'll come back with a per-job
                  estimate and availability.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/request-estimate"
                  data-testid="contact-cta-estimate"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 transition-colors"
                >
                  Request an Estimate <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="contact-cta-call"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#34D399] text-[#0F172A] font-semibold hover:bg-[#10B981] transition-colors"
                >
                  <Phone className="w-4 h-4" /> Call / Text Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
